import { Component, EventEmitter, Output, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { min, Subscription } from 'rxjs';
import { SharedDataService } from '../shared-data.service';
import { LAW, LEGAL_SITUATION, ESTABLISHMENT_TYPE, INVESTMENT_NATURE } from '../laws-and-legal-situation-constants';
import { FoundationServiceService } from '../../features/services/foundation-service.service';
import { FileUploaderService } from '../../core/services/file-uploader.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TabRoutes } from '../modals/tab-routes.enum';
import { FileUploadResult } from '../file-upload/file-upload.component';
import { ToasterService } from '../toaster.service';

@Component({
  selector: 'app-bank-certificate',
  templateUrl: './bank-certificate.component.html',
  styleUrls: ['./bank-certificate.component.scss'],
})
export class BankCertificateComponent implements OnInit, OnDestroy, OnChanges {
  checked: boolean = false;
  isLaw72: boolean = false;
  isLaw159: boolean = false;
  legalSituation: any;
  establishmentType: any;
  investmentNature: any;
  isEditable: boolean = true;
  isCheckboxDisabled: boolean = false;
  paidCapital: number | undefined;

  banks: any[] = [];
  form!: FormGroup;
  filePath: string;
  isSubmitting = false;
  private subscriptions: Subscription[] = [];
  private uploadSubscription?: Subscription;
  isReadOnly: boolean = false;
  maxDate: Date = new Date();
  @Input() existingBankCertificateUrl: string = '';
  @Output() bankCertificate = new EventEmitter<string>();
  @Output() formReady = new EventEmitter<FormGroup>();

  // ── iscore integration state ──────────────────────────────────────────
  isIscoreEnabled: boolean = false;
  isIscoreLoading: boolean = false;
  iscoreDataLoaded: boolean = false;
  iscoreError: string | null = null;

  /** Everything except certificateNumber/bankName — stays disabled the whole time integration is ON */
  private readonly iscoreBlockedControls = ['certificateDate', 'bankBranch', 'companyAccountNumber', 'paidCapital'];

  constructor(
    private sharedData: SharedDataService,
    private lookupService: FoundationServiceService,
    private fileUploaderService: FileUploaderService,
    private fb: FormBuilder,
    private toaster: ToasterService
  ) {
    this.filePath = TabRoutes.PreEstablishment;
  }

  ngOnInit() {
    const noteSub = this.sharedData.noteSections$.subscribe((noteSections: string[]) => {
      const stepCode = 'PRE_FOUNDATION';
      const hasNote = noteSections.includes(stepCode);
      const status = this.sharedData.getRequestStatus();
      if (status === 'PENDING_EDIT') {
        if (hasNote === true) {
          this.isReadOnly = false;
          if (this.legalSituation?.code === LEGAL_SITUATION.LimitedLiability) {
            this.isCheckboxDisabled = false;
          }
        }
        else {
          this.isReadOnly = true;
        }
      } else if (status === 'DRAFT') {
        this.isReadOnly = false;
      } else if (status === '') {
        this.isReadOnly = false;
      }
      else {
        this.isReadOnly = true;
      }
      console.log('📝 PRE_FOUNDATION readonly:', this.isReadOnly);
    });
    this.subscriptions.push(noteSub);

    this.form = this.fb.group({
      idDocument: [null, Validators.required],
      certificateNumber: ['', Validators.required],
      certificateDate: ['', Validators.required],
      bankName: ['', Validators.required],
      bankBranch: ['', Validators.required],
      companyAccountNumber: [''],
      paidCapital: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
    this.formReady.emit(this.form);
    // const currentDate = new Date();
    // this.today = currentDate.toISOString().split('T')[0];

    this.lookupService.getLookupByType('BANK').subscribe((res) => {
      this.banks = res;
    });

    this.lookupService.iScoreIntegrationEnabled().subscribe({
      next: (res: any) => {
        this.isIscoreEnabled = !!(res?.data ?? res);
        this.syncIscoreFieldsDisabledState();
      },
      error: () => {
        // fail-safe: keep manual entry if flag lookup fails
        this.isIscoreEnabled = false;
        this.syncIscoreFieldsDisabledState();
      }
    });

    this.sharedData.selectedLaw$.subscribe((law) => {
      if (law.code === LAW.Law72) {
        this.isLaw72 = true;
      } else {
        this.isLaw159 = true;
      }
    });

    const legalSub = this.sharedData.selectedLegalSituation$.subscribe((situation) => {
      this.legalSituation = situation;
      this.updatePaidCapital();
    });

    const establishSub = this.sharedData.selectedEstablishmentType$.subscribe((type) => {
      this.establishmentType = type;
      this.updatePaidCapital();
    });

    const investSub = this.sharedData.selectedInvestmentNature$.subscribe((nature) => {
      this.investmentNature = nature;
      this.updatePaidCapital();
    });

    this.subscriptions.push(legalSub, establishSub, investSub);

    const isSpecialLegalSituation = [
      LEGAL_SITUATION.OnePersonCompany,
      LEGAL_SITUATION.JointStock,
      LEGAL_SITUATION.PartnershipByShares
    ].includes(this.legalSituation?.code);

    const isNormal =
      this.establishmentType?.code === ESTABLISHMENT_TYPE.Normal;

    this.checked = isSpecialLegalSituation && isNormal;

    this.isCheckboxDisabled = this.checked || this.isReadOnly;
    // this.updatePaidCapital();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When parent passes existing URL, patch it to the form
    if (changes['existingBankCertificateUrl'] && this.existingBankCertificateUrl) {
      this.form?.patchValue({ idDocument: this.existingBankCertificateUrl });
    }
  }

  ngOnDestroy(): void {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  getDefaultValue(): number {
    if (
      this.isLaw159 &&
      (this.legalSituation?.code === LEGAL_SITUATION.JointStock ||
        this.legalSituation?.code === LEGAL_SITUATION.PartnershipByShares)
    ) {
      this.isEditable = true;
      return 10;
    }

    if (
      this.isLaw72 &&
      this.legalSituation?.code === LEGAL_SITUATION.JointStock &&
      (this.establishmentType?.code === ESTABLISHMENT_TYPE.Normal ||
        this.establishmentType?.code === ESTABLISHMENT_TYPE.Conversion) &&
      (this.investmentNature?.code === INVESTMENT_NATURE.Internal ||
        this.investmentNature?.code === INVESTMENT_NATURE.FreeZone)
    ) {
      this.isEditable = true;
      return 10;
    }

    if (
      (this.isLaw159 &&
        (this.legalSituation?.code === LEGAL_SITUATION.LimitedLiability ||
          this.legalSituation?.code === LEGAL_SITUATION.OnePersonCompany)) ||
      (this.isLaw72 &&
        (this.legalSituation?.code === LEGAL_SITUATION.LimitedLiability ||
          this.legalSituation?.code === LEGAL_SITUATION.OnePersonCompany))
    ) {
      this.isEditable = false;
      return 100;
    }

    this.isEditable = true;
    return 0;
  }

  updatePaidCapital() {
    if (this.isReadOnly) return;

    // Add null checks
    if (!this.legalSituation) {
      console.warn('Legal situation not yet available');
      return;
    }

    const defaultValue = this.getDefaultValue();
    this.paidCapital = defaultValue;

    // Update form control
    this.form.patchValue({ paidCapital: defaultValue }, { emitEvent: false });

    console.log('Updated paidCapital to:', defaultValue); // Debug log

    // isEditable may have just changed — re-evaluate paidCapital's disabled state
    this.syncIscoreFieldsDisabledState();
  }

  // ── iscore integration ─────────────────────────────────────────────────

  /** Enables the trigger button once certificateNumber and bankName are filled */
  get isIscoreTriggerReady(): boolean {
    return (
      !!this.form.get('certificateNumber')?.valid &&
      !!this.form.get('bankName')?.valid
    );
  }

  /**
   * Whether the iScore-sourced fields (rest of the form, files included) should stay
   * disabled. These never re-enable once integration is ON — they're read-only,
   * API-populated display fields, not user-editable, even after a successful load.
   */
  get isIscoreFieldsBlocked(): boolean {
    return this.isIscoreEnabled;
  }

  /** Whether submission should wait for a successful iScore load (fields stay disabled either way) */
  get isIscorePendingLoad(): boolean {
    return this.isIscoreEnabled && !this.iscoreDataLoaded;
  }

  /**
   * Fields bound via formControlName must be disabled/enabled through the FormControl
   * itself, not a template [disabled] binding — Angular's FormControlDirective re-syncs
   * the DOM disabled state from the control on every change-detection cycle and silently
   * overrides the template binding otherwise (same bug class fixed earlier for the
   * bankName select — see project notes). Applies to app-file-upload too, since it's a
   * ControlValueAccessor bound the same way.
   */
  private syncIscoreFieldsDisabledState() {
    if (!this.form) return;

    this.iscoreBlockedControls.forEach((name) => {
      const ctrl = this.form.get(name);
      if (!ctrl) return;

      // paidCapital has its own independent editability rule on top of the iScore block
      const shouldDisable = name === 'paidCapital'
        ? this.isIscoreFieldsBlocked || !this.isEditable
        : this.isIscoreFieldsBlocked;

      if (shouldDisable) {
        ctrl.disable({ emitEvent: false });
      } else {
        ctrl.enable({ emitEvent: false });
      }
    });
  }

  onLoadIscoreData() {
    if (this.isReadOnly || !this.isIscoreTriggerReady || this.isIscoreLoading) return;

    const requestId = this.sharedData.getRequestId();
    if (requestId == null) {
      const msg = 'تعذر الحصول على رقم الطلب';
      this.iscoreError = msg;
      this.toaster.error(msg);
      return;
    }

    const selectedBankId = this.form.get('bankName')?.value;
    const selectedBank = this.banks?.find((b: any) => b.id == selectedBankId || b.code == selectedBankId);
    const bankIscoreCode = selectedBank?.iscoreCode;

    if (!bankIscoreCode) {
      const msg = 'رمز إيسكور غير متوفر للبنك المحدد';
      this.iscoreError = msg;
      this.toaster.error(msg);
      return;
    }

    this.isIscoreLoading = true;
    this.iscoreError = null;

    const payload = {
      certificateNumber: this.form.get('certificateNumber')?.value,
      bankId: String(bankIscoreCode),
      requestId: requestId
    };

    console.log('Fetching iScore data with payload:', payload);

    this.lookupService.getIScoreCertificateData(payload).subscribe({
      next: (res: any) => {
        console.log('iScore response received:', res);

        const data = res?.data ?? res;

        const cert = data?.bankCertificate;

        this.form.patchValue({
          certificateDate: null,
          bankBranch: null,
          paidCapital: null,
        });
        this.paidCapital = 0;
        this.bankCertificate.emit('');

        this.form.patchValue({
          certificateDate: cert?.certificateDate,
          bankBranch: cert?.branchName,
          paidCapital: cert?.capitalPercentage,
        });

        if (cert?.capitalPercentage != null) {
          this.paidCapital = cert.capitalPercentage;
        }

        this.iscoreDataLoaded = true;
        this.isIscoreLoading = false;
        this.toaster.success('تم تحميل بيانات الشهادة البنكية بنجاح');
        this.syncIscoreFieldsDisabledState();
      },
      error: (err) => {
        console.error('iScore API Error:', err);
        const status = err?.status || err?.error?.status || err?.error?.statusCode;
        const errorMessage =
          status === 500
            ? err?.error?.detail
            : err?.error?.message ||
            err?.error?.error?.message ||
            err?.message ||
            'حدث خطأ أثناء استرجاع بيانات الشهادة البنكية';

        this.iscoreError = errorMessage;
        this.iscoreDataLoaded = false;
        this.isIscoreLoading = false;
        this.toaster.error("برجاء التحقق من بيانات الشهاده البنكيه");
        this.syncIscoreFieldsDisabledState();
      }
    });
  }

  onSubmit() {
    if (this.isReadOnly) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isIscorePendingLoad) return;

    this.isSubmitting = true;

    const formData = new FormData();
    formData.append('idDocument', this.form.get('idDocument')?.value);
    formData.append('certificateNumber', this.form.get('certificateNumber')?.value);
    formData.append('certificateDate', this.form.get('certificateDate')?.value);
    formData.append('bankName', this.form.get('bankName')?.value);
    formData.append('bankBranch', this.form.get('bankBranch')?.value);
    formData.append('companyAccountNumber', this.form.get('companyAccountNumber')?.value);
    formData.append('paidCapital', this.form.get('paidCapital')?.value);
    formData.append('filePath', this.filePath);
  }

  onUploadComplete(event: any) {
    if (this.isReadOnly) return;
    const filePath = event?.url || '';
    this.bankCertificate.emit(filePath);
  }

  onFileRemoved() {
    if (this.isReadOnly) return;
    const filePath = '';
    this.bankCertificate.emit(filePath);
  }
  isFormCecked() {
    this.checked = !this.checked;
    if (!this.checked) {
      if (this.uploadSubscription) {
        this.uploadSubscription.unsubscribe();
        this.uploadSubscription = undefined;
      }
      this.form.reset();
      this.iscoreDataLoaded = false;
      this.iscoreError = null;
      this.syncIscoreFieldsDisabledState();
    }
  }

  private getBankCertificateImage(base64String: string, fileName: string = 'bank_certificate.pdf'): void {
    if (!base64String || !this.filePath) {
      return;
    }

    const file = this.base64ToFile(base64String, fileName);
    if (!file) {
      return;
    }

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }

    this.uploadSubscription = this.fileUploaderService
      .uploadFile(this.filePath, file, {}, fileName)
      .subscribe({
        next: (progress) => {
          if (progress.state === 'DONE') {
            const uploadedUrl = progress.response?.data;
            if (uploadedUrl) {
              this.form.patchValue({ idDocument: uploadedUrl });
              this.bankCertificate.emit(uploadedUrl);
            }
            this.uploadSubscription = undefined;
          }
        },
        error: (err) => {
          console.error('Upload bank certificate error:', err);
          this.toaster.error('حدث خطأ أثناء رفع ملف الشهادة البنكية');
          this.uploadSubscription = undefined;
        }
      });
  }

  private base64ToFile(base64String: string, fileName: string = 'bank_certificate.pdf'): File | null {
    if (!base64String) return null;
    try {
      const cleanBase64 = base64String.includes('base64,')
        ? base64String.split('base64,')[1]
        : base64String;

      const sanitizedBase64 = cleanBase64.replace(/\s+/g, '');
      const byteCharacters = atob(sanitizedBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], fileName, { type: 'application/pdf' });
    } catch (e) {
      console.error('Error converting base64 to File:', e);
      return null;
    }
  }
}